import { addLinesToLogGroups, type LogGroups } from "@/routes/environments/log-streams/log-groups";
import { isFetchError } from "@/utils/errors";
import { useCallback, useEffect, useState } from "react";
import { streamLogsFromUrl } from "@/hooks/log-streaming/utils";
import type { UseStreamLogsOptions, UseStreamLogsResult } from "@/hooks/log-streaming/types";

/**
 * Generic base hook for streaming logs from any resource type.
 *
 * Uses the Strategy Pattern with adapters to handle resource-specific differences
 * (environments, prebuilds, workflows, etc.) while keeping the core streaming
 * logic in one place.
 *
 * To add a new resource type:
 * 1. Create an adapter implementing LogStreamAdapter<YourType> in adapters.ts
 * 2. Create a thin wrapper hook that calls useStreamLogsBase with your adapter
 *
 * @example
 * ```typescript
 * // See adapters.ts for examples of environmentAdapter and prebuildAdapter
 *
 * // Create a new adapter
 * export const workflowAdapter: LogStreamAdapter<PlainWorkflow> = {
 *     getLogsUrl: (workflow) => workflow?.status?.logsUrl,
 *     getResourceId: (workflow) => workflow?.id,
 *     getPhase: (workflow) => workflow?.status?.phase,
 *     isEarlyPhase: (phase) => !phase || phase === WorkflowPhase.PENDING,
 *     useLogsToken: (id, phase) => useCreateWorkflowLogsToken(id, phase),
 * };
 *
 * // Create a wrapper hook
 * export function useStreamWorkflowLogs(options: { workflow?: PlainWorkflow; maxLinesPerGroup?: number }) {
 *     return useStreamLogsBase({
 *         resource: options.workflow,
 *         adapter: workflowAdapter,
 *         maxLinesPerGroup: options.maxLinesPerGroup,
 *     });
 * }
 * ```
 */
export function useStreamLogsBase<T>(options: UseStreamLogsOptions<T>): UseStreamLogsResult {
    const { resource, adapter, maxLinesPerGroup, overrideLogsUrl } = options;

    const [error, setError] = useState<Error | null>(null);
    const [logGroups, setLogGroups] = useState<LogGroups>({});
    const [loadingMessage, setIsLoadingMessage] = useState<string | undefined>();

    // Use adapter to extract resource-specific data
    // Allow overriding logs URL for cases like service/task logs
    const logsUrl = overrideLogsUrl ?? adapter.getLogsUrl(resource);
    const resourceId = adapter.getResourceId(resource);
    const phase = adapter.getPhase(resource);

    // Use adapter's token hook
    const {
        data: logAccessToken,
        isPending: logAccessTokenPending,
        error: logAccessTokenError,
    } = adapter.useLogsToken(resourceId, phase);

    const waitAndRenderRetry = useCallback(async (seconds: number, signal: AbortSignal) => {
        for (let i = seconds; i >= 0 && !signal.aborted; i--) {
            setIsLoadingMessage(i === 0 ? `retrying now` : `retrying in ${i}s`);
            await new Promise((resolve) => setTimeout(resolve, 1000));
        }
    }, []);

    useEffect(() => {
        const hasLogs = Object.keys(logGroups).length > 0;
        if (hasLogs) {
            return;
        }

        if (adapter.isEarlyPhase(phase)) {
            setIsLoadingMessage("Waiting for logs to become available...");
            return;
        }

        if (!logsUrl) {
            setIsLoadingMessage("No logs available");
            return;
        }

        if (logAccessTokenPending) {
            setIsLoadingMessage("Requesting log access...");
            return;
        }

        setIsLoadingMessage("Loading logs...");
    }, [logsUrl, phase, logAccessTokenPending, logGroups, adapter]);

    useEffect(() => {
        const abort = new AbortController();

        if (!logsUrl || !logAccessToken) {
            return;
        }

        void (async () => {
            let repeat = true;
            while (repeat) {
                try {
                    await streamLogsFromUrl(
                        abort.signal,
                        logsUrl,
                        logAccessToken,
                        ({ clearLogs, error, loadingMessage, logLinesToAdd }) => {
                            if (clearLogs) {
                                setLogGroups({});
                            }
                            if (error || error === null) {
                                setError(error);
                                if (isFetchError(error) && error.status === 404) {
                                    // For 404s we don't show an error, but just retry
                                    setError(null);
                                }
                            }
                            if (logLinesToAdd) {
                                setLogGroups((prevLogGroups) =>
                                    addLinesToLogGroups(prevLogGroups, logLinesToAdd, maxLinesPerGroup ?? 1000),
                                );
                            }
                            if (typeof loadingMessage === "string") {
                                setIsLoadingMessage(loadingMessage);
                            }
                        },
                    );
                    // no network error, no need to retry
                    repeat = false;
                } catch {
                    // retry after 3s
                    await waitAndRenderRetry(3, abort.signal);

                    // consider aborted state
                    repeat = !abort.signal.aborted;
                }
            }
        })();

        return () => {
            abort.abort("Unmounted");
        };
    }, [logAccessToken, logsUrl, maxLinesPerGroup, waitAndRenderRetry]);

    if (!logsUrl || logAccessTokenPending) {
        return {
            loadingMessage: loadingMessage,
        };
    }

    return {
        error: error || logAccessTokenError || undefined,
        loadingMessage: loadingMessage,
        logGroups: logGroups,
    };
}
